import type { MouseEvent } from 'react';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Badge, cn } from '@eombor/ui';
import { t } from '../../shared/i18n/index.js';
import { BrandMark } from '../../shared/brand/BrandMark.js';
import { activeNavTo, isPlainLeftClick, navEntry, visibleNavItems } from './nav-model.js';

export interface SidebarProps {
  /** Ruxsat tekshiruvi — odatda `usePermission()` dan keladi. */
  can: (permission: string) => boolean;
  collapsed: boolean;
  onToggle: () => void;
  /** Joriy manzil; berilmasa hech bir element yonmaydi. */
  activePath?: string;
  /**
   * Bo'lim yonidagi son (masalan o'qilmagan bildirishnomalar).
   *
   * Kalit — `NavItem.to`. Nol yoki yo'q bo'lsa belgi umuman chizilmaydi:
   * "0" raqami e'tiborni tortadi, lekin hech narsa aytmaydi.
   */
  badges?: Readonly<Record<string, number>>;
  /** Xodimga bo'lim biriktirilganmi — «Mening omborim» shunga qarab chiziladi. */
  hasSubdivision?: boolean;
  /**
   * Berilsa — bosilganda sahifa qayta yuklanmaydi (SPA o'tishi). Berilmasa
   * havola oddiy `href` bo'lib qoladi va shunda ham ishlaydi.
   */
  onNavigate?: (to: string) => void;
}

/**
 * Chapdagi doimiy navigatsiya — `md` (768px) VA UNDAN KENG ekranlarda.
 *
 * Telefonda bu panel umuman chizilmaydi: u yerda pastki qator
 * (`MobileTabBar`) va «Menyu» (`MobileNavDrawer`) ishlaydi. Tanlovni
 * `useMobileNav` qiladi.
 *
 * Ilgari bu yerda boshqacha edi: telefonda ham tor belgili ustun bo'lib
 * turardi, chunki ustma-ust ochiladigan menyuni qo'lqopli qo'l bilan
 * yopish qiyin. Sabab haq edi, lekin yechim noto'g'ri joyga qo'yilgandi —
 * tor ustun ekranning eng qimmat chekkasini yeb, bo'lim nomlarini esa
 * baribir ko'rsatmasdi. Pastki qator ikkalasini ham hal qiladi: kundalik
 * to'rt bo'lim doim bosh barmoq ostida, qolgani esa pastdan ochiladigan
 * menyuda — u ham xuddi shu joydan.
 */
export const Sidebar = ({
  can,
  collapsed,
  onToggle,
  activePath = '',
  badges = {},
  hasSubdivision = false,
  onNavigate,
}: SidebarProps) => {
  const visible = visibleNavItems(can, hasSubdivision);
  const activeTo = activeNavTo(visible, activePath);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>, to: string): void => {
    if (onNavigate === undefined || !isPlainLeftClick(event)) return;
    event.preventDefault();
    onNavigate(to);
  };

  return (
    <aside
      data-collapsed={collapsed ? 'true' : 'false'}
      className={cn(
        // Karkasning uchinchi qismi. `z` YO'Q edi — ya'ni `z-base` da
        // turardi va sahifadagi yopishqoq panel uning ustiga chiqa
        // olardi. Geometriya bo'yicha ular kesishmaydi, lekin qatlam
        // tartibi butun karkas uchun bir xil bo'lishi kerak: keyin
        // qo'shilgan soyali panel bu farqni ko'rsatib qo'yardi.
        'sticky top-0 z-[var(--z-chrome)] flex h-dvh shrink-0 flex-col',
        'border-r border-border bg-surface',
        'transition-[width] duration-[var(--motion-base)] ease-[var(--ease-standard)]',
        // Yig'ilgan panel — faqat belgilar; yoyilgani — belgi va nom.
        collapsed
          ? 'w-[var(--size-sidebar-collapsed)]'
          : 'w-[var(--size-sidebar)]',
      )}
    >
      <div
        className={cn(
          'flex h-[var(--size-header)] shrink-0 items-center gap-2.5 border-b border-border',
          collapsed ? 'justify-center px-2' : 'justify-start px-3',
        )}
      >
        <BrandMark className="text-accent" />
        <span
          className={cn(
            'min-w-0 truncate text-base font-semibold tracking-tight text-text',
            collapsed ? 'hidden' : undefined,
          )}
        >
          {t('app.name')}
        </span>
      </div>

      <nav aria-label={t('sidebar.label')} className="min-h-0 flex-1 overflow-y-auto py-2">
        <ul className="flex flex-col gap-0.5 px-2">
          {visible.map((item) => {
            const label = t(item.label);
            const active = item.to === activeTo;
            const Icon = item.icon;
            const count = badges[item.to] ?? 0;

            return (
              <li key={item.to}>
                <a
                  href={navEntry(item)}
                  // Matn yashiringanda ham nom yo'qolmaydi — ekran o'quvchi
                  // va ustiga olib borilgan sichqoncha ikkalasi uchun. Son
                  // ham nomga qo'shiladi: belgi faqat ko'z uchun qolmasin.
                  aria-label={count > 0 ? `${label} (${count})` : label}
                  title={count > 0 ? `${label} (${count})` : label}
                  aria-current={active ? 'page' : undefined}
                  onClick={(event) => handleClick(event, navEntry(item))}
                  className={cn(
                    'relative flex h-[var(--size-control)] items-center gap-3',
                    'rounded-[var(--radius-sm)] text-sm font-medium',
                    'transition-[background-color,color] duration-[var(--motion-fast)]',
                    'ease-[var(--ease-standard)]',
                    collapsed ? 'justify-center px-2' : 'justify-start px-3',
                    active
                      ? 'bg-accent-soft text-accent'
                      : 'text-text-muted hover:bg-surface-2 hover:text-text',
                  )}
                >
                  {/* Aktiv bo'limning chap qirrasidagi 2px belgi. Ro'yxatning
                      `px-2` bo'shlig'idan tashqariga chiqadi — chiziq panelning
                      o'z qirrasida turadi va uzoqdan ham ko'zga tashlanadi. */}
                  <span
                    aria-hidden
                    className={cn(
                      'absolute -left-2 top-1/2 h-6 w-[3px] -translate-y-1/2',
                      'rounded-r-[var(--radius-pill)]',
                      active ? 'bg-accent' : 'bg-transparent',
                    )}
                  />
                  <span className="relative flex shrink-0 items-center">
                    <Icon size={18} aria-hidden />
                    {/* Panel yig'ilganda son sig'maydi — o'rnida kichik nuqta
                        qoladi: "bu yerda ish bor" degan xabar yo'qolmasin. */}
                    {count > 0 ? (
                      <span
                        aria-hidden
                        className={cn(
                          'absolute -top-0.5 -right-1 size-2 rounded-[var(--radius-pill)]',
                          'bg-danger',
                          collapsed ? undefined : 'hidden',
                        )}
                      />
                    ) : null}
                  </span>
                  <span className={cn('truncate', collapsed ? 'sr-only' : undefined)}>
                    {label}
                  </span>
                  {count > 0 ? (
                    <Badge
                      tone="danger"
                      mono
                      className={cn('ml-auto', collapsed ? 'hidden' : 'inline-flex')}
                    >
                      {count}
                    </Badge>
                  ) : null}
                </a>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="shrink-0 border-t border-border p-2">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          aria-label={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
          title={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
          className={cn(
            'flex h-[var(--size-control-sm)] w-full items-center gap-3',
            'rounded-[var(--radius-sm)] px-2 text-sm text-text-faint',
            'transition-[background-color,color] duration-[var(--motion-fast)]',
            'ease-[var(--ease-standard)] hover:bg-surface-2 hover:text-text',
            collapsed ? 'justify-center' : 'justify-start',
          )}
        >
          {collapsed ? (
            <ChevronsRight size={16} aria-hidden />
          ) : (
            <ChevronsLeft size={16} aria-hidden />
          )}
          <span className={cn('truncate', collapsed ? 'sr-only' : undefined)}>
            {t('sidebar.collapse')}
          </span>
        </button>
      </div>
    </aside>
  );
};
